import {
  createOperationScopeSnapshot,
  type AsyncOperationAcknowledgement,
  type AsyncOperationSummary,
  type AsyncOperationsSummary,
  type OperationKey,
  type QueryGroup,
  type ResponseData,
  useInvalidateApiQueries,
  useTeam,
  useTeamQuery,
} from '@vrplatform/data';
import {
  hideNotification,
  showNotification,
  showSuccessNotification,
  showWarnNotification,
  updateErrorNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { useCallback, useEffect, useMemo } from 'react';
import {
  useActiveOperationForResource,
  useOperationTrackerStore,
  useRecoverOperations,
  useTrackOperation,
} from './operationTracker.hooks';
import type {
  OperationEffect,
  TrackedOperation,
} from './operationTracker.types';

type JournalRefreshOperationType = Extract<
  AsyncOperationAcknowledgement['type'],
  'reservation-journal-refresh' | 'transaction-journal-refresh'
>;

type JournalRefreshSourceQueryGroup = Extract<
  QueryGroup,
  'reservations' | 'deposits' | 'expenses' | 'fees'
>;

type JournalRefreshResponse =
  | ResponseData<'post:/reservations/journal-entries'>
  | ResponseData<'post:/transactions/journal-entries'>;

const EMPTY_OPERATIONS: AsyncOperationsSummary = {
  counts: { total: 0, queued: 0, running: 0, completed: 0, failed: 0 },
  items: [],
};

const getFailureMessage = (operation: TrackedOperation) => {
  if (operation.status === 'failed') {
    return (
      operation.failure?.message ??
      'The journal refresh could not be completed.'
    );
  }

  return 'We could not confirm whether the journal refresh completed.';
};

export const useJournalRefreshOperation = ({
  operationType,
  sourceQueryGroup,
}: {
  operationType: JournalRefreshOperationType;
  sourceQueryGroup: JournalRefreshSourceQueryGroup;
}) => {
  const [team] = useTeam();
  const teamQuery = useTeamQuery();
  const invalidate = useInvalidateApiQueries(
    operationType === 'reservation-journal-refresh'
      ? ['reservations', 'fees', 'owner-statements', 'reports', 'operations']
      : [sourceQueryGroup, 'reports']
  );
  const store = useOperationTrackerStore();
  const trackOperation = useTrackOperation();
  const scope = useMemo(
    () =>
      createOperationScopeSnapshot({
        apiTarget: 'regional',
        dataRegion: team.dataRegion,
        regionalApiBaseUrl: team.apiBaseUrl,
        requesterTeamId: team.id,
      }),
    [team.apiBaseUrl, team.dataRegion, team.id]
  );
  const effects = useMemo<readonly OperationEffect[]>(
    () => [
      {
        id: `${operationType}:${team.id}:invalidate-journal-data`,
        kind: 'api',
        target: { scope: 'team', teamIds: [team.id] },
        groups:
          operationType === 'reservation-journal-refresh'
            ? ['reservations', 'fees', 'owner-statements', 'reports']
            : [sourceQueryGroup, 'reports'],
        runOn:
          operationType === 'reservation-journal-refresh'
            ? ['completed', 'failed']
            : ['completed'],
      },
    ],
    [operationType, sourceQueryGroup, team.id]
  );
  const effectsByType = useMemo(
    () => ({ [operationType]: effects }),
    [effects, operationType]
  );
  const resolveScope = useCallback(
    (operation: AsyncOperationSummary) =>
      operation.type === operationType &&
      (operation.status === 'queued' || operation.status === 'running') &&
      operation.resource?.type === 'team' &&
      operation.resource.role === 'target' &&
      operation.resource.id === team.id
        ? scope
        : null,
    [operationType, scope, team.id]
  );
  useRecoverOperations({
    summary: teamQuery.data?.team.operations ?? EMPTY_OPERATIONS,
    resolveScope,
    effectsByType,
  });
  const activeOperation = useActiveOperationForResource(
    scope,
    { id: team.id, role: 'target', type: 'team' },
    operationType
  );
  const notificationId = `journal-refresh:${team.id}:${operationType}`;

  const monitorOperation = useCallback(
    (operationKey: OperationKey) => {
      if (operationType === 'reservation-journal-refresh') return;
      const monitorId = `${notificationId}:${operationKey}:monitor`;
      if (!store.getState().claimOperationEffect(operationKey, monitorId)) {
        return;
      }

      hideNotification({ id: notificationId });
      showNotification({
        id: notificationId,
        title: 'Refreshing journal entries...',
        message: 'You can keep working while the refresh finishes.',
        loading: true,
        autoClose: false,
        radius: 10,
      });

      let unsubscribe: () => void = () => undefined;
      const settle = (operation: TrackedOperation | undefined) => {
        if (!operation) {
          unsubscribe();
          hideNotification({ id: notificationId });
          return;
        }
        if (operation.status === 'queued' || operation.status === 'running') {
          return;
        }

        unsubscribe();
        if (operation.status === 'completed') {
          updateSuccessNotification({
            id: notificationId,
            title: 'Journal entries refreshed',
            message: 'Journal entries have been refreshed successfully.',
          });
          return;
        }

        updateErrorNotification({
          id: notificationId,
          title: 'Failed to refresh journal entries',
          message: `${getFailureMessage(operation)} You can retry the refresh.`,
        });
      };

      // The tracker owns this subscription so terminal feedback survives a
      // route or confirmation-modal unmount. It releases itself on stop/reset.
      unsubscribe = store.subscribe((state) => {
        settle(state.operations[operationKey]);
      });
      settle(store.getState().operations[operationKey]);
    },
    [notificationId, operationType, store]
  );

  useEffect(() => {
    if (activeOperation) monitorOperation(activeOperation.key);
  }, [activeOperation, monitorOperation]);

  const canStart = useCallback(
    (ids: readonly string[]) => {
      if (activeOperation) {
        showWarnNotification({
          title: 'Journal refresh already in progress',
          message:
            'Wait for the current journal refresh to finish, then retry.',
        });
        return false;
      }
      if (!ids.length) {
        showWarnNotification({
          title: 'No rows selected',
          message: 'Select at least one row to refresh journal entries.',
        });
        return false;
      }
      if (ids.length > 1_000) {
        showWarnNotification({
          title: 'Select 1,000 or fewer rows',
          message: 'Reduce the selection before refreshing journal entries.',
        });
        return false;
      }

      return true;
    },
    [activeOperation]
  );

  const acceptResponse = useCallback(
    (response: JournalRefreshResponse) => {
      if ('total' in response) {
        invalidate();
        if (operationType !== 'reservation-journal-refresh')
          showSuccessNotification({
            title: 'Journal entries refreshed',
            message: 'Journal entries have been refreshed successfully.',
          });
        return;
      }

      if (
        response.type !== operationType ||
        (response.resource?.type === 'team' &&
          response.resource.id !== team.id) ||
        (operationType !== 'reservation-journal-refresh' &&
          (response.resource?.type !== 'team' ||
            response.resource.role !== 'target'))
      ) {
        throw new Error('Journal refresh acknowledgement has an invalid scope');
      }

      const operationKey = trackOperation({
        acknowledgement: response,
        scope,
        effects,
      });
      if (operationType === 'reservation-journal-refresh') invalidate();
      monitorOperation(operationKey);
    },
    [
      effects,
      invalidate,
      monitorOperation,
      operationType,
      scope,
      team.id,
      trackOperation,
    ]
  );

  return {
    acceptResponse,
    activeOperation,
    canStart,
    requestHeaders: {
      'x-data-region': scope.placement.dataRegion,
      'x-team-id': scope.requesterTeamId,
    },
  };
};
